import { BLOCK_ICONS } from "@/lib/blockIcons";
import type { SiteTemplateKey } from "@/lib/siteTemplates";
import { getTemplateStyles } from "@/lib/siteTemplateStyles";

type FeatureItem = {
  icon?: string;
  title?: string;
  body?: string;
};

export function FeatureGridBlock({
  content,
  template = "classic",
}: {
  content: Record<string, unknown>;
  template?: SiteTemplateKey;
}) {
  const heading = String(content.heading ?? "");
  const intro = String(content.intro ?? "");
  const items = (Array.isArray(content.items) ? content.items : []) as FeatureItem[];
  const columns = Math.min(Math.max(Number(content.columns ?? 3), 1), 4);
  const styles = getTemplateStyles(template);

  if (!heading && !intro && items.length === 0) return null;

  const colClass =
    columns === 1
      ? "md:grid-cols-1"
      : columns === 2
        ? "md:grid-cols-2"
        : columns === 4
          ? "md:grid-cols-2 lg:grid-cols-4"
          : "md:grid-cols-2 lg:grid-cols-3";

  return (
    <section className={styles.textGroup.section}>
      {(heading || intro) && (
        <div className="mb-[calc(var(--space-unit)*1.5)] max-w-3xl">
          {heading && (
            <h2
              className="font-bold text-[var(--color-text)]"
              style={{ fontFamily: "var(--font-heading)", fontSize: "var(--text-h2)", lineHeight: "var(--line-heading)" }}
            >
              {heading}
            </h2>
          )}
          {intro && <p className="mt-2 text-[var(--color-text)]/70">{intro}</p>}
        </div>
      )}
      <div className={`grid grid-cols-1 gap-[calc(var(--space-unit)*1.5)] ${colClass}`}>
        {items.map((item, index) => {
          const Icon = item.icon ? BLOCK_ICONS[item.icon] : undefined;
          return (
            <div key={index} className={styles.textGroup.card}>
              {Icon && (
                <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-full bg-[rgb(var(--color-primary))]/10 text-[rgb(var(--color-primary))]">
                  <Icon className="h-5 w-5" />
                </div>
              )}
              {item.title && (
                <h3
                  className="mb-1 font-semibold text-[var(--color-text)]"
                  style={{ fontFamily: "var(--font-heading)" }}
                >
                  {item.title}
                </h3>
              )}
              {item.body && <p className="text-sm text-[var(--color-text)]/70">{item.body}</p>}
            </div>
          );
        })}
      </div>
    </section>
  );
}
